/**
 * VendorRegisterPage
 *
 * Vendor registration wizard with two flows:
 * 1. Unauthenticated users: 3 steps (Account, Business, Review)
 * 2. Authenticated users: 2 steps (Business, Review) - skips account creation
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Stack } from '@/components/ui/layout';
import { Title, Text, Badge, Anchor } from '@/components/ui/core';
import { Stepper } from '@/components/ui/navigation';
import { IconUser, IconBuilding, IconCheck, IconSparkles, IconLogout } from '@tabler/icons-react';
import { FadeIn } from '@/components/animations';
import { useVendorRegistrationService } from '@actschurch/ccms-sdk/react';
import type { VendorRegistrationRequest, RegisterMyVendorRequest } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useVendorStore } from '@/stores/vendorStore';

import { AccountInfoStep, type AccountInfoData } from './components/AccountInfoStep';
import { BusinessInfoStep, type BusinessInfoData } from './components/BusinessInfoStep';
import { ReviewStep } from './components/ReviewStep';
import { RegistrationSuccess } from './components/RegistrationSuccess';
import { RegistrationProgress, type RegistrationStep } from './components/RegistrationProgress';

// Registration result data to pass to success screen
interface RegistrationResult {
  displayName?: string | null;
  username?: string | null;
  password?: string;
  emailConfirmationRequired?: boolean;
  isExistingUser?: boolean;
}

// Initial form data
const initialAccountData: AccountInfoData = {
  userEmail: '',
  password: '',
  confirmPassword: '',
  userFirstName: '',
  userSurname: '',
  userCellphone: '',
};

const initialBusinessData: BusinessInfoData = {
  businessName: '',
  vendorTypeID: 0,
  phoneNumber: '',
  email: '',
  website: '',
};

export default function VendorRegisterPage() {
  const registrationService = useVendorRegistrationService();
  const { isAuthenticated, user, logout, refresh } = useEnrichedAuth();

  // Get vendor store action to trigger refetch after registration
  const triggerRefetch = useVendorStore((state) => state.triggerRefetch);

  // For authenticated users, we skip the account step
  const isAuthenticatedFlow = isAuthenticated && !!user;
  const totalSteps = isAuthenticatedFlow ? 2 : 3;

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep>('creating_account');
  const [registrationResult, setRegistrationResult] = useState<RegistrationResult | null>(null);

  // Form data - for authenticated users, pre-fill from user info
  const [accountData, setAccountData] = useState<AccountInfoData>(() => {
    if (isAuthenticatedFlow) {
      return {
        ...initialAccountData,
        userEmail: user?.email || '',
        userFirstName: user?.firstName || '',
        userSurname: user?.surname || '',
        userCellphone: user?.cellphone || '',
      };
    }
    return initialAccountData;
  });

  const [businessData, setBusinessData] = useState<BusinessInfoData>(() => ({
    ...initialBusinessData,
    // Pre-fill business email with user email for authenticated users
    email: isAuthenticatedFlow ? (user?.email || '') : '',
  }));

  // Step navigation
  const nextStep = () => setActiveStep((current) => Math.min(current + 1, totalSteps - 1));
  const prevStep = () => setActiveStep((current) => Math.max(current - 1, 0));

  // Data change handlers
  const handleAccountDataChange = (data: Partial<AccountInfoData>) => {
    setAccountData((prev) => ({ ...prev, ...data }));
  };

  const handleBusinessDataChange = (data: Partial<BusinessInfoData>) => {
    setBusinessData((prev) => ({ ...prev, ...data }));
  };

  // Submit registration
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setRegistrationStep('creating_account');

    try {
      if (isAuthenticatedFlow) {
        // Authenticated flow: Create vendor linked to existing user
        setRegistrationStep('setting_up_vendor');

        const request: RegisterMyVendorRequest = {
          vendorTypeID: businessData.vendorTypeID,
          businessName: businessData.businessName,
          phoneNumber: businessData.phoneNumber,
          email: businessData.email,
          website: businessData.website || null,
        };

        const response = await registrationService.registerMy(request);

        if (response.success) {
          setRegistrationStep('linking_account');

          // Wait briefly for backend to process role assignment via RabbitMQ
          // The AssignVendorRoleCommand is processed asynchronously
          await new Promise(resolve => setTimeout(resolve, 1000));

          setRegistrationStep('finalizing');

          // Refresh JWT token to get updated roles/permissions (includes new "Vendor" role)
          try {
            await refresh();
          } catch (refreshErr) {
            console.warn('Token refresh failed, user may need to re-login for full access:', refreshErr);
          }

          // Trigger vendor context refetch so X-Vendor-ID header is set for subsequent API calls
          triggerRefetch();

          // Store result for success screen
          setRegistrationResult({
            displayName: response.displayName,
            username: response.username,
            emailConfirmationRequired: response.emailConfirmationRequired,
          });

          // Small delay to show finalization step
          await new Promise(resolve => setTimeout(resolve, 500));

          setIsComplete(true);
        } else {
          setSubmitError(response.message || 'Registration failed. Please try again.');
        }
      } else {
        // Anonymous flow: Create user + vendor
        // The backend now creates user first, then vendor
        const request: VendorRegistrationRequest = {
          // User data
          userEmail: accountData.userEmail,
          password: accountData.password,
          userFirstName: accountData.userFirstName,
          userSurname: accountData.userSurname,
          userCellphone: accountData.userCellphone || null,

          // Business data
          vendorTypeID: businessData.vendorTypeID,
          businessName: businessData.businessName,
          phoneNumber: businessData.phoneNumber,
          email: businessData.email,
          website: businessData.website || null,
        };

        // Simulate progress steps (actual backend does all in one call)
        // but we show user-friendly progress indicators
        setRegistrationStep('creating_account');
        await new Promise(resolve => setTimeout(resolve, 500));

        const response = await registrationService.register(request);

        if (response.success) {
          setRegistrationStep('setting_up_vendor');
          await new Promise(resolve => setTimeout(resolve, 400));

          setRegistrationStep('linking_account');
          await new Promise(resolve => setTimeout(resolve, 400));

          setRegistrationStep('finalizing');
          await new Promise(resolve => setTimeout(resolve, 300));

          // Store result for success screen - only include password for NEW users
          // If isExistingUser is true, they need to use their existing password
          setRegistrationResult({
            displayName: response.displayName,
            username: response.username,
            password: response.isExistingUser ? undefined : accountData.password,
            emailConfirmationRequired: response.emailConfirmationRequired ?? !response.isExistingUser,
            isExistingUser: response.isExistingUser,
          });

          setIsComplete(true);
        } else {
          setSubmitError(response.message || 'Registration failed. Please try again.');
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Registration failed. Please try again.';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show progress screen while submitting
  if (isSubmitting) {
    return (
      <RegistrationProgress currentStep={registrationStep} />
    );
  }

  // Show success screen if complete
  if (isComplete) {
    return (
      <RegistrationSuccess
        businessName={businessData.businessName}
        email={isAuthenticatedFlow ? (user?.email || businessData.email) : accountData.userEmail}
        isAuthenticated={isAuthenticatedFlow}
        displayName={registrationResult?.displayName}
        username={registrationResult?.username}
        password={registrationResult?.password}
        emailConfirmationRequired={registrationResult?.emailConfirmationRequired}
        isExistingUser={registrationResult?.isExistingUser}
      />
    );
  }

  // Render stepper based on flow
  if (isAuthenticatedFlow) {
    // Authenticated flow: 2 steps (Business, Review)
    return (
      <Stack gap="lg">
        {/* Header */}
        <FadeIn delay={300}>
          <Stack align="center" gap="xs">
            <Badge
              leftSection={<IconSparkles size={12} />}
              variant="gradient"
              gradient={{ from: 'indigo', to: 'violet' }}
              size="lg"
            >
              Become a Vendor
            </Badge>
            <Title order={2} ta="center" mt="md">
              Register your business
            </Title>
            <Text c="dimmed" size="sm" ta="center">
              Hi {user?.firstName}! Complete the form below to register as a vendor.
            </Text>
          </Stack>
        </FadeIn>

        {/* Stepper - 2 steps for authenticated users */}
        <FadeIn delay={400}>
          <Stepper
            active={activeStep}
            onStepClick={setActiveStep}
            gradientVariant="primary"
            allowNextStepsSelect={false}
            size="sm"
          >
            <Stepper.Step
              label="Business"
              description="Company info"
              icon={<IconBuilding size={18} />}
            >
              <BusinessInfoStep
                data={businessData}
                onDataChange={handleBusinessDataChange}
                onNext={nextStep}
                onBack={undefined}
                showBackButton={false}
              />
            </Stepper.Step>

            <Stepper.Step
              label="Review"
              description="Confirm & submit"
              icon={<IconCheck size={18} />}
            >
              <ReviewStep
                accountData={accountData}
                businessData={businessData}
                onBack={prevStep}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                submitError={submitError}
                isAuthenticated={isAuthenticatedFlow}
              />
            </Stepper.Step>
          </Stepper>
        </FadeIn>

        {/* Logout Link for authenticated users */}
        <FadeIn delay={500}>
          <Text ta="center" size="sm">
            Don't want to register?{' '}
            <Anchor
              component="button"
              type="button"
              onClick={() => logout()}
              fw={500}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconLogout size={14} />
              Sign out
            </Anchor>
          </Text>
        </FadeIn>
      </Stack>
    );
  }

  // Anonymous flow: 3 steps (Account, Business, Review)
  return (
    <Stack gap="lg">
      {/* Header */}
      <FadeIn delay={300}>
        <Stack align="center" gap="xs">
          <Badge
            leftSection={<IconSparkles size={12} />}
            variant="gradient"
            gradient={{ from: 'indigo', to: 'violet' }}
            size="lg"
          >
            Become a Vendor
          </Badge>
          <Title order={2} ta="center" mt="md">
            Register your business
          </Title>
          <Text c="dimmed" size="sm" ta="center">
            Complete the steps below to register as a vendor
          </Text>
        </Stack>
      </FadeIn>

      {/* Stepper - 3 steps for anonymous users */}
      <FadeIn delay={400}>
        <Stepper
          active={activeStep}
          onStepClick={setActiveStep}
          gradientVariant="primary"
          allowNextStepsSelect={false}
          size="sm"
        >
          <Stepper.Step
            label="Account"
            description="Your details"
            icon={<IconUser size={18} />}
          >
            <AccountInfoStep
              data={accountData}
              onDataChange={handleAccountDataChange}
              onNext={nextStep}
            />
          </Stepper.Step>

          <Stepper.Step
            label="Business"
            description="Company info"
            icon={<IconBuilding size={18} />}
          >
            <BusinessInfoStep
              data={businessData}
              onDataChange={handleBusinessDataChange}
              onNext={nextStep}
              onBack={prevStep}
            />
          </Stepper.Step>

          <Stepper.Step
            label="Review"
            description="Confirm & submit"
            icon={<IconCheck size={18} />}
          >
            <ReviewStep
              accountData={accountData}
              businessData={businessData}
              onBack={prevStep}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              submitError={submitError}
              isAuthenticated={false}
            />
          </Stepper.Step>
        </Stepper>
      </FadeIn>

      {/* Login Link */}
      <FadeIn delay={500}>
        <Text ta="center" size="sm">
          Already have an account?{' '}
          <Anchor component={Link} to="/auth/login" fw={500}>
            Sign in
          </Anchor>
        </Text>
      </FadeIn>
    </Stack>
  );
}
